import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Form, Container, Row, Col, Button } from "react-bootstrap";
import PropertyList from "containers/property/PropertyList";
import { PacmanLoader } from "react-spinners";
import { get } from "aws-amplify/api";
import DatePicker from "react-datepicker";
import Select from "react-select";
import Moment from "moment";
import { createElementIdFactory } from "utils/elementIds";
import "containers/common/List.css";
import "containers/common/Table.css";

const initialFormData = {
  propertyKey: "",
  buildingName: "",
  address: "",
  address2: "",
  stnolow: "",
  stnohigh: "",
  description: "",
  zoning: "",
  historical: "",
  title: "",
  saleshistory: "",
  valuations: "",
  tenant: "",
  proposal: "",
  saleappendix: "",
  photorequired: "",
  activequeryfollowup: null,
  perpetual: "",
  saledateentered: null,
  salesheetfollowup: null,
  ownername: "",
  browselistidfilter: "",
  state: "",
  scope: "",
  hasleasingagent: "",
  missingphoto: "",
};

function PropertySearch({ isAuthenticated, userPermissions, user }) {
  const [historicalSearchOptions, setHistoricalSearchOptions] = useState([]);
  const [selectedHistoricalSearchOption, setSelectedHistoricalSearchOption] = useState(null);
  const [cityscopeOptions, setCityscopeOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [selectedCityscopeOption, setSelectedCityscopeOption] = useState(null);
  const [selectedStateOption, setSelectedStateOption] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [formData, setFormData] = useState(initialFormData);

  const hasTrustProof = userPermissions && userPermissions.trust_proof === true;
  const isReadyToRender = isAuthenticated && hasTrustProof;
  const createId = useMemo(() => createElementIdFactory("property-search"), []);
  const lookupFilter = useMemo(() => ({ user, screen: "property" }), [user]);

  const loadSearchLookups = useCallback(async () => {
    try {
      const [searchesResponse, cityscopesResponse, statesResponse] = await Promise.all([
        get({ apiName: "colin", path: "/searches", options: { queryParams: lookupFilter } }).response,
        get({ apiName: "colin", path: "/cityscopes/dropdown" }).response,
        get({ apiName: "colin", path: "/states" }).response,
      ]);

      const [searches, cityscopes, states] = await Promise.all([
        searchesResponse.body.json(),
        cityscopesResponse.body.json(),
        statesResponse.body.json(),
      ]);

      if (Array.isArray(searches)) {
        setHistoricalSearchOptions(searches);
        if (searches.length > 0) {
          setSelectedHistoricalSearchOption(searches[0]);
        }
      }
      if (Array.isArray(cityscopes)) {
        setCityscopeOptions(cityscopes);
      }
      if (Array.isArray(states)) {
        setStateOptions(states);
      }
    } catch (e) {
      alert(e + ". Please refresh the page (F5) to try again.");
    }
  }, [lookupFilter]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    document.title = "Cityscope Property Search";
    loadSearchLookups();
  }, [isAuthenticated, loadSearchLookups]);

  function serializeDate(value) {
    if (!value) return "";
    const momentValue = Moment(value);
    return momentValue.isValid() ? momentValue.local().format() : "";
  }

  function parseDate(value) {
    if (!value) return null;
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  function normalizeQueryValue(value) {
    if (value === "Invalid date" || value === null || value === undefined) {
      return "";
    }
    return value;
  }

  function parseBoolean(value) {
    if (typeof value === "boolean") {
      return value;
    }
    if (typeof value === "number") {
      return value === 1;
    }
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (["true", "1", "yes", "on"].includes(normalized)) {
        return true;
      }
      if (["false", "0", "no", "off", "", "null", "undefined"].includes(normalized)) {
        return false;
      }
      return false;
    }
    return false;
  }

  function getSearchParam(searchParams, keys, fallback = "") {
    if (!searchParams || typeof searchParams !== "object") {
      return fallback;
    }

    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(searchParams, key)) {
        return searchParams[key];
      }
    }

    const normalizedLookup = {};
    for (const existingKey of Object.keys(searchParams)) {
      normalizedLookup[existingKey.toLowerCase().replace(/_/g, "")] = searchParams[existingKey];
    }

    for (const key of keys) {
      const normalizedKey = key.toLowerCase().replace(/_/g, "");
      if (Object.prototype.hasOwnProperty.call(normalizedLookup, normalizedKey)) {
        return normalizedLookup[normalizedKey];
      }
    }

    return fallback;
  }

  async function getProperties(filter) {
    const propertyFilter = {};
    for (const key in filter) {
      propertyFilter[key] = normalizeQueryValue(filter[key]);
    }

    const response = await get({
      apiName: "colin",
      path: "/properties",
      options: { queryParams: propertyFilter },
    }).response;

    try {
      const apiProperties = await response.body.json();
      return apiProperties;
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
      return [];
    }
  }

  function buildPropertyQuery(searchData) {
    const saleAppendixValue = searchData.saleappendix ?? "";

    return {
      propertykey: searchData.propertyKey,
      buildingname: searchData.buildingName,
      address: searchData.address,
      address2: searchData.address2,
      stnolow: searchData.stnolow,
      stnohigh: searchData.stnohigh,
      description: searchData.description,
      zoning: searchData.zoning,
      historical: searchData.historical,
      title: searchData.title,
      saleshistory: searchData.saleshistory,
      valuations: searchData.valuations,
      tenant: searchData.tenant,
      proposal: searchData.proposal,
      saleappendix: saleAppendixValue,
      photorequired: searchData.photorequired,
      activequeryfollowup: serializeDate(searchData.activequeryfollowup),
      perpetual: searchData.perpetual,
      saledateentered: serializeDate(searchData.saledateentered),
      salesheetfollowup: serializeDate(searchData.salesheetfollowup),
      ownername: searchData.ownername,
      browselistid: searchData.browselistidfilter,
      state: searchData.state,
      scope: searchData.scope,
      hasleasingagent: searchData.hasleasingagent,
      missingphoto: searchData.missingphoto,
      user,
      screen: "property",
    };
  }

  async function runSearch(searchData) {
    setIsLoading(true);
    try {
      const nextProperties = await getProperties(buildPropertyQuery(searchData));
      setProperties(Array.isArray(nextProperties) ? nextProperties : []);
    } finally {
      setIsLoading(false);
    }
  }

  const updateTableState = useCallback((updatedData, setName) => {
    if (setName === "properties") {
      setProperties(updatedData);
    }
  }, []);

  function handleChange(event) {
    const { id, name, value } = event.target;

    const deriveFieldFromId = (inputId) => {
      const idText = String(inputId || "");
      const match = idText.match(/input-(.+)$/);
      if (!match || !match[1]) {
        return "";
      }

      return match[1].replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
    };

    const fieldName = name || deriveFieldFromId(id);
    if (!fieldName) {
      return;
    }

    setFormData((previousFormData) => ({
      ...previousFormData,
      [fieldName]: value,
    }));
  }

  async function handleSubmit(event) {
    if (event) {
      event.preventDefault();
    }
    await runSearch(formData);
  }

  function clearFilters() {
    setFormData(initialFormData);
    setSelectedCityscopeOption(null);
    setSelectedStateOption(null);
    setSelectedHistoricalSearchOption(null);
  }

  function handleSearchChange(option) {
    setSelectedHistoricalSearchOption(option);

    if (!option || !option.search_parameters) {
      return;
    }

    const parsedSearchParameters = JSON.parse(option.search_parameters);
    const parsedPropertyKey = getSearchParam(parsedSearchParameters, ["propertyKey", "propertykey"], "");
    const parsedBuildingName = getSearchParam(parsedSearchParameters, ["buildingName", "buildingname"], "");
    const parsedSaleAppendix = getSearchParam(parsedSearchParameters, ["saleappendix"], getSearchParam(option, ["saleappendix"], ""));

    // Convert boolean search fields to empty string when false (UAT parity)
    const booleanSearchFields = ["photorequired", "perpetual", "hasleasingagent", "missingphoto"];
    const parsedBooleans = {};
    booleanSearchFields.forEach(field => {
      const parsed = parseBoolean(parsedSearchParameters[field]);
      parsedBooleans[field] = parsed ? true : "";
    });

    const nextFormData = {
      ...initialFormData,
      ...parsedSearchParameters,
      propertyKey: parsedPropertyKey,
      buildingName: parsedBuildingName,
      saleappendix: parsedSaleAppendix,
      activequeryfollowup: parseDate(parsedSearchParameters.activequeryfollowup),
      perpetual: parsedBooleans.perpetual,
      saledateentered: parseDate(parsedSearchParameters.saledateentered),
      salesheetfollowup: parseDate(parsedSearchParameters.salesheetfollowup),
      photorequired: parsedBooleans.photorequired,
      hasleasingagent: parsedBooleans.hasleasingagent,
      missingphoto: parsedBooleans.missingphoto,
    };

    setFormData(nextFormData);

    const nextState = parsedSearchParameters.state || null;
    const nextScope = parsedSearchParameters.scope || null;

    setSelectedStateOption(nextState ? { value: nextState, label: nextState } : null);
    setSelectedCityscopeOption(nextScope ? { value: nextScope, label: nextScope } : null);

    runSearch(nextFormData);
  }

  function handleStateChange(option) {
    if (option) {
      setSelectedStateOption(option);
      setFormData((previousFormData) => ({
        ...previousFormData,
        state: option.value,
      }));
    } else {
      setSelectedStateOption(null);
      setFormData((previousFormData) => ({
        ...previousFormData,
        state: "",
      }));
    }
  }

  function handleCityscopeChange(option) {
    if (option) {
      setSelectedCityscopeOption(option);
      setFormData((previousFormData) => ({
        ...previousFormData,
        scope: option.value,
      }));
    } else {
      setSelectedCityscopeOption(null);
      setFormData((previousFormData) => ({
        ...previousFormData,
        scope: "",
      }));
    }
  }

  function handleDateChange(date, name) {
    setFormData((prev) => ({
      ...prev,
      [name]: date && !Number.isNaN(date.getTime()) ? date : null,
    }));
  }

  function handleCheckBoxChange(event) {
    const { name, checked } = event.target;
    // For boolean search fields, use empty string when unchecked (not checked)
    // to match UAT behavior and ensure no false positives in search results
    const booleanSearchFields = ["photorequired", "perpetual", "hasleasingagent", "missingphoto"];
    const value = checked ? true : (booleanSearchFields.includes(name) ? "" : false);
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  const resultsSection = useMemo(() => {
    if (isLoading) {
      return (
        <div id={createId("loading-container")} className="outer-div">
          <div className="sweet-loading inner-div">
            <PacmanLoader
              cssOverride={{ display: "block", margin: "0 auto" }}
              size={20}
              color={"#e03c31"}
              loading={isLoading}
            />
          </div>
        </div>
      );
    }

    return (
      <PropertyList
        isAuthenticated={isAuthenticated}
        isLoading={false}
        data={properties}
        controlType="table"
        updateTableState={updateTableState}
      />
    );
  }, [createId, isAuthenticated, isLoading, properties, updateTableState]);

  function renderProperties() {
    return (
      <Container id={createId("page")} fluid>
        <Row id={createId("header-row")}>
          <Col sm={12} md={12} lg={12}>
            <h6 id={createId("title")}>Cityscope Property Search</h6>
          </Col>
        </Row>
        <Row id={createId("layout-row")}>
          <Col md={3} lg={3}>
            <Form id={createId("form")} onSubmit={handleSubmit}>
              <Row id={createId("actions-row")}>
                <Col>
                  <Button type="submit" id={createId("button-search")}>
                    Search
                  </Button>
                </Col>
                <Col>
                  <Button id={createId("button-clear")} type="button" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                </Col>
              </Row>
              <hr />

              <Row>
                <Col>
                  <Form.Group className="form-group">
                    <Form.Control
                      id={createId("input-property-key")}
                      value={formData.propertyKey}
                      onChange={handleChange}
                      as="input"
                      type="text"
                      placeholder="Cityscope Reference starting"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col lg={3}>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-stnolow")}
                      type="input"
                      value={formData.stnolow}
                      onChange={handleChange}
                      placeholder="St No. Low"
                    />
                  </Form.Group>
                </Col>

                <Col lg={3}>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-stnohigh")}
                      type="input"
                      value={formData.stnohigh}
                      onChange={handleChange}
                      placeholder="St No. High"
                    />
                  </Form.Group>
                </Col>

                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-address")}
                      type="input"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="Street Name starting"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Select
                      inputId={createId("select-state")}
                      value={selectedStateOption}
                      onChange={handleStateChange}
                      options={stateOptions}
                      placeholder="State"
                      isClearable
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Select
                      inputId={createId("select-scope")}
                      value={selectedCityscopeOption}
                      onChange={handleCityscopeChange}
                      options={cityscopeOptions}
                      placeholder="Scope"
                      isClearable
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-address2")}
                      type="input"
                      value={formData.address2}
                      onChange={handleChange}
                      placeholder="Street Name 2 starting - use for cnr properties"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-building-name")}
                      type="input"
                      value={formData.buildingName}
                      onChange={handleChange}
                      placeholder="Building Name containing"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-ownername")}
                      type="input"
                      value={formData.ownername}
                      onChange={handleChange}
                      placeholder="Owner Name containing"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-saleappendix")}
                      type="input"
                      value={formData.saleappendix}
                      onChange={handleChange}
                      placeholder="Sales Appendix equals"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-tenant")}
                      type="input"
                      value={formData.tenant}
                      onChange={handleChange}
                      placeholder="Tenant Text (including pre/postamble) containing"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-description")}
                      type="input"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Description containing"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-zoning")}
                      type="input"
                      value={formData.zoning}
                      onChange={handleChange}
                      placeholder="Zoning containing"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-historical")}
                      type="input"
                      value={formData.historical}
                      onChange={handleChange}
                      placeholder="Historic Listings containing"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-title")}
                      type="input"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Title Details containing"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-saleshistory")}
                      type="input"
                      value={formData.saleshistory}
                      onChange={handleChange}
                      placeholder="Sales History containing"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-valuations")}
                      type="input"
                      value={formData.valuations}
                      onChange={handleChange}
                      placeholder="For Lease Text (including pre/postamble) containing"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      id={createId("input-proposal")}
                      type="input"
                      value={formData.proposal}
                      onChange={handleChange}
                      placeholder="Proposals Text containing"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col lg={6}>
                  <Form.Group>
                    <DatePicker
                      id={createId("date-activequeryfollowup")}
                      selected={formData.activequeryfollowup}
                      onChange={(e) => handleDateChange(e, "activequeryfollowup")}
                      dateFormat="dd/MM/yyyy"
                      className="form-control"
                      placeholderText="Query Resolve Date Before"
                      isClearable
                    />
                  </Form.Group>
                </Col>

                <Col lg={6}>
                  <Form.Group>
                    <Form.Check
                      id={createId("checkbox-perpetual")}
                      checked={formData.perpetual}
                      onChange={handleCheckBoxChange}
                      label="Exclude Perpetual"
                      type="checkbox"
                      name="perpetual"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col lg={6}>
                  <Form.Group>
                    <div className="customDatePickerWidth">
                      <DatePicker
                        id={createId("date-salesheetfollowup")}
                        selected={formData.salesheetfollowup}
                        onChange={(e) => handleDateChange(e, "salesheetfollowup")}
                        dateFormat="dd/MM/yyyy"
                        className="form-control"
                        placeholderText="Sale Sheet Followup Date Before"
                        isClearable
                      />
                    </div>
                  </Form.Group>
                </Col>

                <Col>
                  <Form.Group>
                    <div className="customDatePickerWidth">
                      <DatePicker
                        id={createId("date-saledateentered")}
                        selected={formData.saledateentered}
                        onChange={(e) => handleDateChange(e, "saledateentered")}
                        dateFormat="dd/MM/yyyy"
                        className="form-control"
                        placeholderText="Sale Entered Date Since"
                        isClearable
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group>
                    <Form.Check
                      id={createId("checkbox-photorequired")}
                      checked={formData.photorequired}
                      onChange={handleCheckBoxChange}
                      label="Photo Required"
                      type="checkbox"
                      name="photorequired"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col lg={6}>
                  <Form.Group>
                    <Form.Check
                      id={createId("checkbox-hasleasingagent")}
                      checked={formData.hasleasingagent}
                      onChange={handleCheckBoxChange}
                      label="Has Leasing Agent?"
                      type="checkbox"
                      name="hasleasingagent"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col lg={6}>
                  <Form.Group>
                    <Form.Check
                      id={createId("checkbox-missingphoto")}
                      checked={formData.missingphoto}
                      onChange={handleCheckBoxChange}
                      label="Property Missing Photo?"
                      type="checkbox"
                      name="missingphoto"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Form.Group as={Col}>
                  <Form.Label className="text-right">Historical Searches</Form.Label>
                  <Select
                    inputId={createId("select-historical-search")}
                    value={selectedHistoricalSearchOption}
                    onChange={handleSearchChange}
                    options={historicalSearchOptions}
                    isClearable
                  />
                </Form.Group>
              </Row>
            </Form>
          </Col>

          <Col md={9} lg={9}>{resultsSection}</Col>
        </Row>
      </Container>
    );
  }

  return (
    <div id={createId("root")} className="PropertySearch">
      {isReadyToRender ? renderProperties() : null}
    </div>
  );
}

export default PropertySearch;
